import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  HostListener,
  Inject,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PageUtils } from '../../../common/page.utils';
import { AssessmentPanelUtils } from '../../../common/ui.utils';
import { TestletType } from '../../../constants/testlet-type.enum';
import { APP_CONFIG, AppConfig } from '../../../models/app-config';
import { AssessmentPlayerService } from '../../../services/player.service';
import { noNav } from '../../../store/actions/page-content.actions';
import { PageContentState } from '../../../store/models/assessment-models';
import { selectCurrentUpdateAssessmentState } from '../../../store/selectors/assessment.selectors';
import { selectActionButtons, selectDisplayActionButtons } from '../../../store/selectors/page-content.selectors';
import { selectLoadingState } from '../../../store/selectors/page-loading.selectors';
import { LearnosityPlayerComponent } from '../../common/learnosity-player/learnosity-player.component';
import { PageComponent } from '../../common/page/page.component';
import { PageConfig } from '../../common/page/page.models';
import { TestletActionButtonsConfig } from '../../common/testlet-action-buttons/models/testlet-action-buttons.models';
import { TestletActionButtonsComponent } from '../../common/testlet-action-buttons/testlet-action-buttons.component';
import { BaseTestletHandler } from '../base-testlet-handler';

@Component({
  selector: 'missing-numbers',
  templateUrl: './missing-numbers.component.html',
  styleUrls: ['./missing-numbers.component.scss', '../styles/testlet.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, PageComponent, LearnosityPlayerComponent, TestletActionButtonsComponent],
})
export class MissingNumbersComponent extends BaseTestletHandler implements OnDestroy, AfterViewInit {
  protected updatedAssessmentConfig = this.store.select(selectCurrentUpdateAssessmentState);
  protected displayActionButtons$: Observable<boolean> = this.store.select(selectDisplayActionButtons);
  protected testletActionButtonsConfig$: Observable<TestletActionButtonsConfig> = this.store
    .select(selectActionButtons)
    .pipe(
      map((buttons) => ({
        buttonTrackingContext: 'missing-numbers',
        ...buttons,
      })),
    );
  protected pageConfig$: Observable<PageConfig> = this.store
    .select(selectLoadingState)
    .pipe(map((state) => ({ loading: state.loadingData.loading })));

  constructor(
    @Inject(DOCUMENT) private document: Document,
    protected playerService: AssessmentPlayerService,
    protected store: Store<PageContentState>,
    private pageUtils: PageUtils,
    @Inject(APP_CONFIG) private readonly config: AppConfig,
  ) {
    super(playerService, store);
  }

  ngAfterViewInit() {
    // Set Event Listeners action methods here.
    this.eventHandlers = {
      questionLoadEventNotifier$: (itemPosition) => {
        this.setQuestionPageControls(itemPosition);
        this.insertSubmitButton();
        this.insertClearButton();
      },
      questionChangeEventNotifier$: (response) => this.toggleSubmitButton(response.responses),
    };
    this.updatedAssessmentConfig.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((updateAssessment) => {
      if (
        updateAssessment != null &&
        !updateAssessment.loading &&
        updateAssessment.error == null &&
        updateAssessment.data?.config != null &&
        updateAssessment.data?.nextTestlet == 'Missing Numbers'
      ) {
        const testlet = updateAssessment.data;
        this.initializeListeners(
          JSON.parse(updateAssessment.data.config),
          'MissingNumber',
          testlet.sessionId,
          testlet.assessmentId,
        );
      } else {
        this.pageUtils.navigateToIntroductionPage(!updateAssessment?.loading);
      }
    });

    this.buttonClickSubject
      .pipe(debounceTime(this.config.debounceTime), takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        this.handleDebouncedButtonClick(type);
      });
  }

  ngOnDestroy() {
    this.onDestroy();
  }

  @HostListener('window:keyup', ['$event'])
  public keyUpEventListener(event: KeyboardEvent) {
    event.preventDefault();
    const isEnterKeyDown = event.key === ' ' || event.key === 'Enter';
    if (isEnterKeyDown) {
      (this.document.activeElement as HTMLElement)?.click();
    }
  }

  @HostListener('click', ['$event'])
  public responseClickHandler(event: any) {
    if (event.target.tagName !== 'INPUT') {
      return;
    }
    if (this.playerService.isLastInstruction()) {
      this.store.dispatch(noNav());
    }
    this.navigateToNextItem();
  }

  /**
   * @description Enable/disable "Submit" button based on the "Attempted Status" of the current question.
   */
  private toggleSubmitButton(responses: string[]) {
    const buttonId = `submit-button-${this.currentQuestionIndex}`;
    const button = this.document.getElementById(buttonId) as HTMLButtonElement;
    button.disabled = responses.some((value) => value === null || value === '');
  }

  /**
   * @description Insert a clear button to the Learnosity number pad.
   */
  private insertClearButton() {
    const id = `clear-button-${this.currentQuestionIndex}`;
    const button = AssessmentPanelUtils.createElement(this.document, 'button', 'clear', id);
    AssessmentPanelUtils.setElementAttribute(
      button,
      'btn custom-clr-btn d-block py-3 border-0 mt-3 font-size-16 ml-auto mr-auto',
      'clear button',
      '1',
      false,
    );
    const keypadContainer = this.document.getElementsByClassName('lrn_possibilityList lrn_dragdrop');
    keypadContainer[this.currentQuestionIndex]?.insertAdjacentElement('afterend', button);
    this.setButtonListener(id, 'clear');
  }

  /**
   * @description Insert a submit button to the Learnosity number pad.
   */
  private insertSubmitButton() {
    const id = `submit-button-${this.currentQuestionIndex}`;
    const submitBtn = AssessmentPanelUtils.createElement(this.document, 'button', 'submit', id);
    AssessmentPanelUtils.setElementAttribute(
      submitBtn,
      'btn custom-submit-btn d-block py-3 border-0 text-white rounded-pill text-capitalize ml-auto mr-auto',
      'submit button',
      '1',
      true,
    );
    const keypadContainer = this.document.getElementsByClassName('lrn_possibilityList lrn_dragdrop');
    keypadContainer[this.currentQuestionIndex]?.insertAdjacentElement('afterend', submitBtn);
    this.setButtonListener(id, 'submit');
  }

  /**
   * @description Attach click event handler to the custom button based on the ID.
   * Prevents invoking of any 'touchend' event handlers and then triggers 'click' event.
   */
  private setButtonListener(elementId: string, label: string) {
    this.document.getElementById(elementId)?.addEventListener('click', () => {
      if (label === 'clear') {
        this.clearResponses();
      }
      if (label === 'submit') {
        this.navigateToNextItem();
      }
    });
  }

  /**
   * @description Will reset the response entered by the user.
   * This is achieved by setting focus to each response box and then press the backspace via code.
   */
  private clearResponses() {
    const dropWell = this.document.getElementsByClassName('lrn-well');
    let outerSpan = dropWell[this.currentQuestionIndex].getElementsByClassName(
      'lrn_math_response lrn-dragdrop-group-16',
    );
    if (!outerSpan[0]) {
      outerSpan = dropWell[this.currentQuestionIndex].getElementsByClassName('lrn_cloze_response lrn_dropzone');
    }

    for (const span of Array.from(outerSpan)) {
      const dropSpan = span.getElementsByClassName('lrn_item');
      if (!dropSpan[0]) {
        continue;
      }
      (dropSpan[0] as HTMLElement).click();
      const dropzone = this.document.getElementsByClassName('lrn_possibilityList lrn_dragdrop')[
        this.currentQuestionIndex
      ] as HTMLElement;
      dropzone.click();
    }
  }

  protected readonly TestletType = TestletType;
}
